import React, { useCallback, useContext, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { TableRow, TableCell } from '@material-ui/core';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import _ from 'lodash';
import classNames from 'classnames';

import './EventRow.scss';
import { RootState } from '../../../configs/store';
import SportContext from '../Context/Sport.context';
import BottomMenuContext from 'src/components/BottomMenu/Context/BottomMenu.context';
import EventRowMarket from './EventRowMarket/EventRowMarket';
import MarketsGrid from './MarketsGrid/MarketsGrid';
import Icon from '../../Icon/Icon';
import StatsModal from '../../StatsModal/StatsModal';
import useIsMobile from 'src/hooks/useIsMobile';

function EventRow({ event, tournamentName, collapsed, tournament }) {
  const { selectedMainMarkets, sport: { name }, screen } = useContext(SportContext);
  const { selectedTab, handleOpen } = useContext(BottomMenuContext);
  const [showStats, setShowStats] = useState(false);
  const { matchTrackerSidebar:{ showMatchTracker, eventId } } = useSelector((state: RootState) => state.events);
  const mobile = useIsMobile();
  const { typeOfCode } = useSelector((state: RootState) => state.peripherals);

  // REDUX
  const { selectedEvent } = useSelector((state: RootState) => state.events);
  const { selectedTimezone } = useSelector((state: RootState) => state.odds);
  const dispatch = useDispatch();
  const history = useHistory();
  const eventRowBtn = useRef(null);
  const [toggleModal, setToggleModal] = useState(false);
  const [visibleTracker, setVisibleTracker] = useState(false);
  const [lastPositionOpen, setLastPositionOpen] = useState(0);

  const competitors = event.name ? event.name.split(' - ') : '';
  const date = utcToZonedTime(event.eventStart * 1000, selectedTimezone);

  const addToBetslip = useCallback((market, outcome) => {
    dispatch.betslip.addBet({
      eventCode: event.eventCode,
      odds: outcome.value,
      marketOutcomeCode: outcome.code || outcome.id,
      marketCode: market.marketCode || outcome.marketCode,
      marketName: market.marketName || market.name,
      tournamentName,
      tournament: tournament,
      datetime: event.eventStart * 1000,
      sport: name,
      jsonLabel: outcome.name,
      outcome: outcome.name,
      eventName: event.name,
      type: 'sport',
      eventStatus: event.statusNumber,
      marketStatusNumber: event.statusNumber > 1 ? event.statusNumber : market.statusNumber,
      marketStatusName:  event.statusNumber > 1 ? event.statusName : market.statusName,
      outcomeStatusNumber: outcome.statusNumber,
      outcomeStatusName: outcome.statusName,
      venue: event.venueName
    });
  }, []);

  const removeFromBetslip = useCallback((index) => {
    dispatch.betslip.removeBetAtIndex(index);
  }, []);

  const handleSlipMatchTracker = () => {
    if (!showMatchTracker){
      setVisibleTracker(false);
      dispatch.events.toggleMatchTracker({ name: event.name, showMatchTracker: true, eventId: event.linkId });
    } else {
      dispatch.events.toggleMatchTracker({ name: '', showMatchTracker: false, matchTrackerMinimized: false, eventId: null });
    }
  };
  const handleShowMatchTracket = () => {
    if (showMatchTracker){
      dispatch.events.toggleMatchTracker({ name: '', showMatchTracker: false, matchTrackerMinimized: false, eventId: null });
    }
  };

  const handleTracker = () => {
    if (showMatchTracker){
      dispatch.events.toggleMatchTracker({ name: '', showMatchTracker: false, matchTrackerMinimized: false, eventId: null });
    }
    setVisibleTracker(!visibleTracker);
  };

  const toggleMarketsCollapse = async (e) => {
    e.preventDefault();

    if (event.eventCode.toString() === selectedEvent?.code) {
      handleShowMatchTracket();
      dispatch.events.clearSelectedEvent();
      window.scrollTo(0, lastPositionOpen);
      return ;
    }

    if (!e.target.classList.contains('event-row__event-odds')) {
      await dispatch.events.getSportEventByCode(event.eventCode);

      const offsetBtn = 205;
      const top = eventRowBtn.current.getBoundingClientRect().top + window.scrollY - offsetBtn;

      setLastPositionOpen(top);
      window.scrollTo(0, top);
    }
  };

  const openNewWindow = (name, id) => (e) => {
    e.preventDefault();

    window.open(
      `https://s5.sir.sportradar.com/keithhobexchange/${ localStorage.getItem('locale') || 'en' }/match/${ id }`,
      name,
      'left=0,top=0,width=100,height=100,status,resizable,scrollbars,noopener,noreferrer'
    );
  };

  const openNewModal = (e) => {
    e.stopPropagation();

    setToggleModal(!toggleModal);
  };

  const goToSearchedRacingEvent = () => {
    dispatch.events.resetSearchResults();
    history.push(`/racing/${ event.meetingCode }/${ event.eventCode }`);
  };

  const onMobileSportEventClick = () => {
    if (handleOpen) {
      handleOpen();
    }

    const state = { bottomDrawer: null };
    // We're only interested in using the state if one of the drawers is open.
    if (selectedTab !== '') {
      state.bottomDrawer = selectedTab;
    }

    history.push(`/sports-betting/events/${ event.tournamentCode }/${ event.eventCode }`, state);
  };

  return (
    <>
      <TableRow
        className={
          classNames({
            'event-row': true,
            'event-row__sticky': true,
            'event-row--collapsed': collapsed,
            'event-row--selected': event.eventCode.toString() === selectedEvent?.code && screen === 'desktop',
            'event-row--clickable': ('raceInfos' in event)
          })
        }
        { ...(('raceInfos' in event) && { onClick: goToSearchedRacingEvent }) }
      >
        {
          screen === 'desktop' && (
            <>
              <TableCell align="center" style={ { width: '45px' } } onClick={ toggleMarketsCollapse }>
                <span className="event-row__event-schedule">{ format(date, 'dd/MM') }</span><br />
                <span className="event-row__event-schedule">{ format(date, 'HH:mm') }</span>
              </TableCell>

              <TableCell
                align="center"
                style={ { width: '30px' } }
                className={ classNames({ 'event-row__event-clock': event.isLiveTrading }) }
                onClick={ toggleMarketsCollapse }
              >
                {
                  event.isLiveTrading && <Icon
                    className={
                      classNames({
                        'event-row__is-live-trading' : true,
                        'event-row__is-live-trading--live': event.statusName === 'live'
                      }
                      )
                    }
                    icon="chronometer"
                  />
                }
              </TableCell>
            </>
          )
        }

        <TableCell
          colSpan={ screen === 'desktop' ? 1 : 2 }
          align="right"
          className="event-row__event-name"
          { ...(screen === 'mobile' && event?.tournamentCode && event?.eventCode && { onClick: onMobileSportEventClick }) }
          { ...(screen === 'desktop' && { onClick: toggleMarketsCollapse }) }
        >
          {
            screen === 'mobile' && !('raceInfos' in event) && (
              <div >
                <span className="event-row__event-clock">
                  { event.eventType === 'sportUpcoming' && <Icon icon="chronometer" /> }
                </span>
                <span className="event-row__event-schedule">
                  {
                    !('raceInfos' in event) &&
                    <>
                      { format(date, 'dd/MM') }
                      &nbsp;{ format(date, 'HH:mm') }
                    </>
                  }
                </span>
                {
                  event?.inPlayInfo !== undefined && event.inPlayInfo?.matchStatusCode === 0 &&
                  <span className="event-row__event-schedule--time">
                    { event.inPlayInfo.matchTime }
                  </span>
                }
                {
                  event?.tournamentCode &&
                  event?.eventCode && (
                    <span className={
                      classNames({
                        'event-row__event-markets': true,
                        'event-row__event-markets--selected': event.eventCode.toString() === selectedEvent?.code
                      })
                    }
                    >
                      { event.eventCode.toString() === selectedEvent?.code ? '-' : '+' }{ event.marketCount }
                    </span>
                  )
                }
              </div>
            )
          }
          <div className="event-row__competitors-container">
            <div>
              <div>{ event.competitors ? event.competitors[0]?.name :competitors[0] }</div>
              { event.competitors && <div> { event.competitors[1]?.name }</div> }
              { event.type !== 'sr:season' && !event.competitors && <div>{ competitors[1] }</div> }
            </div>
            <span> { typeOfCode === 'cardCode' ? event.couponCardCode : event.couponCode } </span>
          </div>

        </TableCell>
        <TableCell
          className="event-row__tracker-icons-container"
          align="left"
          title={
            !visibleTracker ? 'Open Match tracker' :
              'Close Match Tracker'
          }
        >
          <Icon
            className={
              classNames({
                'event-row__tracker-icon': true,
                'event-row__tracker-icon--active': visibleTracker
              })
            }
            icon="matchtracker"
            onClick={ handleTracker }
          />
        </TableCell>
        {
          !mobile &&
        <TableCell
          className="event-row__tracker-icons-container"
          align="left"
          title={
            eventId !== event.linkId ?
              'Open Match tracker on Betslip' :
              'Close Match tracker on Betslip'
          }
        >
          <Icon
            className={
              classNames({
                'event-row__tracker-icon': true,
                'event-row__tracker-icon--active': eventId === event.linkId
              })
            }
            icon="trackerBetslip"
            onClick= { handleSlipMatchTracker }
          />
        </TableCell>
        }

        {
          'raceInfos' in event && <TableCell className="event-row__stats" align="right">
            {
              showStats && (
                <div className="event-row__cards">
                  <div className="event-row__cards-container">
                    <Icon
                      className="event-row__card"
                      icon="cards"
                    />
                  </div>
                  <div className="event-row__home-cards">
                    <span>0/0</span>
                  </div>
                  <div className="event-row__away-cards">
                    <span>0/0</span>
                  </div>
                </div>
              )
            }
            <Icon
              icon="liveStats"
              className={
                classNames({
                  'event-row__stats-graph': true,
                  'event-row__stats-graph--white': showStats
                })
              }
              onClick={ () => setShowStats(!showStats) }
            />
            <div className="event-row__score">
              <div className="event-row__score--home">0</div>
              <div className="event-row__score--away">3</div>
            </div>
          </TableCell>
        }
        {
          screen === 'mobile' && !event?.inPlayInfo !== undefined &&
            <TableCell align="center" style={ { width: '30px' } } colSpan={ ('raceInfos' in event) ? 2 : 1 }>
              {
                event.linkId && (
                  <Icon icon="graph" className="event-row__event-graph" onClick={ openNewModal } />
                )
              }
            </TableCell>
        }

        {
          _.map(selectedMainMarkets, (mainMarket, mi) => (
            <EventRowMarket
              key={ mi }
              mainMarket={ mainMarket }
              event={ event }
              removeFromBetslip={ removeFromBetslip }
              addToBetslip={ addToBetslip }
            />
          ))
        }

        {
          screen === 'desktop' && (
            <>
              {
                !('raceInfos' in event) && (
                  <TableCell align="center" style={ { width: '45px' } }>
                    <div
                      className={
                        classNames({
                          'event-row__event-markets': true,
                          'event-row__event-markets--empty': event.markets && event.markets.length === 0,
                          'event-row__event-markets--selected': event.eventCode.toString() === selectedEvent?.code
                        })
                      }
                      ref={ eventRowBtn }
                      onClick={ toggleMarketsCollapse }
                    >
                      { event.eventCode.toString() === selectedEvent?.code ? '-' : '+' }{ event.marketCount }
                    </div>
                  </TableCell>
                )
              }
              <TableCell align="center" style={ { width: '30px' } } colSpan={ ('raceInfos' in event) ? 2 : 1 }>
                {
                  event.linkId && (
                    <Icon icon="graph" className="event-row__event-graph" onClick={ openNewWindow(name, event?.linkId) } />
                  )
                }
              </TableCell>
            </>
          )
        }
      </TableRow>
      <TableRow className={ classNames({ 'event-row': true, 'event-row--collapsed': collapsed }) }>
        {
          ((!collapsed && event.eventCode.toString() === selectedEvent?.code && selectedEvent?.markets) || visibleTracker) && (
            <MarketsGrid
              event={ event }
              removeFromBetslip={ removeFromBetslip }
              addToBetslip={ addToBetslip }
              displayMarkets= { (event.eventCode.toString() === selectedEvent?.code) }
              visibleTracker= { visibleTracker }
            />
          )
        }
      </TableRow>
      {
        toggleModal &&
          <StatsModal linkId={ event?.linkId } toggle={ openNewModal } type="event" />
      }
    </>
  );
}

EventRow.propTypes = {
  event: PropTypes.object,
  tournamentName: PropTypes.string,
  collapsed: PropTypes.bool,
  tournament: PropTypes.object
};

export default React.memo(EventRow);
